import { DEFAULT_WAVE_COLOR } from '@anm/constants/colors';
import noop from '@anm/helpers/noop';
import { Row } from '@anm/styles/helpers';
import React, { useEffect, useRef, useState, ChangeEvent, FormEvent, FC } from 'react';

import { Password, VideoPlayerCallbacks } from '../../types';

import {
  Container,
  FormTitle,
  FormWrapper,
  InputField,
  PasswordForm,
  SubmitButton,
  SubmitError,
  Wrapper
} from './Wrapper';

type ProtectedVideoProps = {
  isPending: boolean;
  isDisabledSubmit?: boolean;
  onSubmit: (pwd: string) => void;
  password?: Password;
  errorMessage?: string;
} & VideoPlayerCallbacks;

const submitError = 'Wrong password. Please try again';

const defaultPasswordState: Password = {
  password: '',
  description: 'Please enter password to watch this video'
};

const ProtectedVideo: FC<ProtectedVideoProps> = ({
  onInit = noop,
  onSubmit,
  isPending,
  errorMessage,
  isDisabledSubmit = false,
  password = defaultPasswordState
}) => {
  const [videoPasswordHint, setVideoPasswordHint] = useState(password);
  const [currentPassword, setCurrentPassword] = useState(password.password);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    onInit();
  }, []);

  useEffect(() => {
    setVideoPasswordHint({
      description: password.description || defaultPasswordState.description
    });
  }, [password.description]);

  const input = useRef<HTMLInputElement>(null);
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isDisabledSubmit) return;

    onSubmit(input.current!.value);
    setTimeout(() => setIsFormSubmitted(true), 0);
  };

  const handlePasswordChange = ({ target }: ChangeEvent<HTMLInputElement>) => setCurrentPassword(target.value);

  const showSubmitError = isFormSubmitted && !isPending;

  return (
    <Wrapper color={DEFAULT_WAVE_COLOR}>
      <Container>
        <FormWrapper>
          <FormTitle title={errorMessage || videoPasswordHint.description!} />
          <Row>
            <PasswordForm onSubmit={handleSubmit} ref={form}>
              <InputField
                ref={input}
                type="password"
                disabled={isDisabledSubmit}
                onChange={handlePasswordChange}
                isIconActive={!!currentPassword}
              />
              <SubmitButton disabled={isDisabledSubmit} />
            </PasswordForm>
            <SubmitError showError={showSubmitError}>{submitError}</SubmitError>
          </Row>
        </FormWrapper>
      </Container>
    </Wrapper>
  );
};

export default ProtectedVideo;
